import { useSyncExternalStore } from 'react';

// D1 Phase 3b — active brand is a single-process bit of UI state
// persisted in localStorage so a refresh remembers the founder's last
// pick. We *don't* put it in the URL — phase 3c will introduce
// /brand/:slug routes if/when that becomes necessary. Today the brand
// is implicit: the chip in TopNav is the source of truth and the
// route loaders read it via getActiveBrandId() to scope their fetches.
//
// Why an external store (and not useState in a provider): route loaders
// in TanStack Router run *before* React renders the tree, so they
// can't read context. A plain global with a subscribe/notify pattern
// lets both the loader and useQuery hooks read the same value.

const KEY = 'magig:activeBrandId';
const listeners = new Set<() => void>();
// Cached snapshot — useSyncExternalStore demands referential stability
// between reads when nothing has changed, so we can't return a fresh
// `localStorage.getItem(...)` string on every subscribe-callback cycle
// (it triggers an infinite re-render). The cache is invalidated whenever
// setActiveBrandId fires.
let cached: string | null | undefined;

function read(): string | null {
  if (typeof localStorage === 'undefined') return null;
  if (cached !== undefined) return cached;
  cached = localStorage.getItem(KEY);
  return cached;
}

export function getActiveBrandId(): string | null {
  return read();
}

export function setActiveBrandId(id: string | null): void {
  if (typeof localStorage === 'undefined') return;
  if (id === null) localStorage.removeItem(KEY);
  else localStorage.setItem(KEY, id);
  cached = id;
  for (const l of listeners) l();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** React hook for the active brand. Re-renders when setActiveBrandId
 *  fires. Server-render returns null (no localStorage); only matters
 *  for tests since we're a pure SPA. */
export function useActiveBrandId(): string | null {
  return useSyncExternalStore(subscribe, read, () => null);
}
